import type { NetworkStatus } from "@apollo/client";
import { useMemo, useState, useEffect } from "react";

import { ValidationState, type HumanValidationEvent } from "../../../API";
import { ISignedUrl } from "../../../common/models/signedUrl";
import { useGetHumanValidatorByTenantTable } from "./useGetHumanValidatorByTenantTable";
import { useGetHumanValidatorTable } from "./useGetHumanValidatorTable";
import { useListenToHumanValidationEvents } from "./useListenToHumanValidationEvents";
import { errorNotification } from "../../../common/variables/notification";

export interface PendingEventRow extends ISignedUrl {
  rowId: string;
  customerId: string;
  locationId: string;
  nodeId: string;
  serviceId: string;
  validationState: ValidationState | null | undefined;
  data: string | null;

  date: string;
  eventId: string;
  eventTime: string;
  model: string;
  cameraName: string;
  mediaOutput: string;
  location: string;
}

interface GetHumanValidatorTableDataInterface {
  rows: PendingEventRow[];
  nextToken?: string | null;
  loading: boolean;
  networkStatus: NetworkStatus;
  fetchMore: any; // Use any to accommodate both query types
}

export const useGetHumanValidatorByTenantTableData = (
  shouldUseCustomerSelectValue: boolean
): GetHumanValidatorTableDataInterface => {
  const { data: listenerData } = useListenToHumanValidationEvents();

  // Track items that have been removed from the queue
  const [removedItems, setRemovedItems] = useState<Set<string>>(new Set());

  // Update removed items when subscription data changes
  useEffect(() => {
    if (listenerData?.listenToHumanValidationEvents) {
      const event = listenerData.listenToHumanValidationEvents;

      // If an item is being validated (not PENDING), add it to removed items
      if (event.validationState !== ValidationState.PENDING) {
        setRemovedItems(prev => new Set(Array.from(prev).concat(event.id)));
      } else {
        // If an item returns to PENDING state, remove it from removed items
        setRemovedItems(prev => {
          const newSet = new Set(Array.from(prev));

          newSet.delete(event.id);

          return newSet;
        });
      }
    }
  }, [listenerData]);

  const {
    pendingEvents: pendingEventsByTenant,
    nextToken: nextTokenByTenant,
    loading: loadingByTenant,
    networkStatus: networkStatusByTenant,
    fetchMore: fetchMoreByTenant,
  } = useGetHumanValidatorByTenantTable();

  const { pendingEvents, nextToken, loading, networkStatus, fetchMore } =
    useGetHumanValidatorTable();

  const rows = useMemo((): PendingEventRow[] => {
    return (
      pendingEvents
        ?.map((item: HumanValidationEvent): PendingEventRow => {
          let eventData = null;

          if (item.data) {
            try {
              eventData = JSON.parse(item.data);
            } catch (error) {
              console.error(error);

              errorNotification();
            }
          }

          const validationState =
            listenerData &&
            listenerData?.listenToHumanValidationEvents?.id === item.id
              ? listenerData?.listenToHumanValidationEvents?.validationState
              : item.validationState;

          return {
            rowId: item.id,
            customerId: item.customerId,
            locationId: item.locationId,
            nodeId: item.nodeId,
            serviceId: item.serviceId,
            validationState,
            data: eventData,

            date: eventData?.Timestamp,
            eventId: item.id,
            eventTime: item?.eventTime,
            model: item.serviceId,
            cameraName: eventData.camera_id,
            mediaOutput: eventData.mediaOutput,
            signedUrlMediaOutput: eventData.signedUrlMediaOutput,
            location: eventData.location,
          };
        })
        .filter((item: PendingEventRow): boolean => {
          // Filter out items that are in the removedItems set
          if (removedItems.has(item.eventId)) {
            return false;
          }

          // removes events that are already being validated
          return listenerData
            ? item.validationState === ValidationState.PENDING
            : !!item;
        }) ?? []
    );
  }, [pendingEvents, listenerData, shouldUseCustomerSelectValue, removedItems]);

  const memoizedSortedItems = useMemo(() => {
    // Sort items to move fire service events to the top
    const fireItems = rows.filter(item => item?.serviceId === "fire");

    const otherItems = rows
      .filter(item => item?.serviceId !== "fire")
      .sort((a, b) => {
        return (a?.serviceId ?? "").localeCompare(b?.serviceId ?? "");
      });

    const sortedItems = fireItems.concat(otherItems);

    return sortedItems;
  }, [rows, shouldUseCustomerSelectValue, listenerData]);

  const rowsByTenant = useMemo((): PendingEventRow[] => {
    return (
      pendingEventsByTenant
        ?.map((item: HumanValidationEvent): PendingEventRow => {
          let eventData = null;

          if (item.data) {
            eventData = JSON.parse(item.data);
          }

          const validationState =
            listenerData &&
            listenerData?.listenToHumanValidationEvents?.id === item.id
              ? listenerData?.listenToHumanValidationEvents?.validationState
              : item.validationState;

          return {
            rowId: item.id,
            customerId: item.customerId,
            locationId: item.locationId,
            nodeId: item.nodeId,
            serviceId: item.serviceId,
            validationState,
            data: eventData,

            date: eventData?.Timestamp,
            eventId: item.id,
            eventTime: item?.eventTime,
            model: item.serviceId,
            cameraName: eventData.camera_id,
            mediaOutput: eventData.mediaOutput,
            signedUrlMediaOutput: eventData.signedUrlMediaOutput,
            location: eventData.location,
          };
        })
        .filter((item: PendingEventRow): boolean => {
          // Filter out items that are in the removedItems set
          if (removedItems.has(item.eventId)) {
            return false;
          }

          // removes events that are already being validated
          return listenerData
            ? item.validationState === ValidationState.PENDING
            : !!item;
        }) ?? []
    );
  }, [
    pendingEventsByTenant,
    listenerData,
    shouldUseCustomerSelectValue,
    removedItems,
  ]);

  const memoizedSortedItemsByTenant = useMemo(() => {
    // Sort items to move fire service events to the top
    const fireItems = rowsByTenant.filter(item => item?.serviceId === "fire");

    const otherItems = rowsByTenant
      .filter(item => item?.serviceId !== "fire")
      .sort((a, b) => {
        return (a?.serviceId ?? "").localeCompare(b?.serviceId ?? "");
      });

    const sortedItems = fireItems.concat(otherItems);

    return sortedItems;
  }, [rowsByTenant, shouldUseCustomerSelectValue, listenerData]);

  return shouldUseCustomerSelectValue
    ? {
        rows: memoizedSortedItemsByTenant,
        nextToken: nextTokenByTenant,
        loading: loadingByTenant,
        networkStatus: networkStatusByTenant,
        fetchMore: fetchMoreByTenant,
      }
    : {
        rows: memoizedSortedItems,
        nextToken: nextToken,
        loading: loading,
        networkStatus: networkStatus,
        fetchMore: fetchMore,
      };
};
